import React, { useState } from 'react';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@frontend/shadcn/components/ui/dropdown-menu';
import { Input } from '@frontend/shadcn/components/ui/input';
import { ChevronDownIcon } from '@revfluence/fresh-icons/solid/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import type { GetAllPrograms_programs } from '@frontend/app/queries/types/GetAllPrograms';
import type { GetCommunitiesQuery_communities } from '@frontend/app/queries/types/GetCommunitiesQuery';
import { InstagramCreator } from '@frontend/gql/social/graphql';

type ProgramItem = GetAllPrograms_programs;
type GroupItem = GetCommunitiesQuery_communities;

export interface CreatorOverviewActionsProps {
  /** The creator data */
  creator: InstagramCreator;
  /** Whether IGDM is active */
  igdmActive?: boolean;
  /** Whether the action menu is disabled */
  disabled?: boolean;
  /** Filtered campaigns data */
  filteredCampaigns: ProgramItem[];
  /** Filtered groups data */
  filteredGroups: GroupItem[];
  /** Campaign search term */
  campaignSearchTerm: string;
  /** Set campaign search term function */
  setCampaignSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  /** Group search term */
  groupSearchTerm: string;
  /** Set group search term function */
  setGroupSearchTerm: React.Dispatch<React.SetStateAction<string>>;
  /** Invite to campaign handler */
  onInviteToCampaign: (program: ProgramItem) => void;
  /** Add to group handler */
  onAddToGroup: (group: GroupItem) => void;
  /** Add to campaign handler */
  onAddToCampaign: (program: ProgramItem) => void;
  /** Whether creator can be invited */
  canBeInvited: boolean;
  /** Get invite disabled reason function */
  getInviteDisabledReason: () => string | null;
}

/**
 * CreatorOverviewActions
 * ----------------------
 * Composable version of the original CreatorOverviewActionMenu.
 *
 * This component provides the same functionality as the original but is designed
 * to work with the new composable system. It can be used as a child of
 * SheetTopBarCreatorActionMenu.
 */
const CreatorOverviewActions: React.FC<CreatorOverviewActionsProps> = ({
  creator: _creator,
  igdmActive: _igdmActive = false,
  disabled = false,
  filteredCampaigns,
  filteredGroups,
  campaignSearchTerm,
  setCampaignSearchTerm,
  groupSearchTerm,
  setGroupSearchTerm,
  onInviteToCampaign,
  onAddToGroup,
  onAddToCampaign,
  canBeInvited,
  getInviteDisabledReason,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  return (
    <DropdownMenu onOpenChange={setIsDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="flex items-center gap-2"
          data-dd-action-name="instagram-creator-overview-actions"
        >
          Actions
          <ChevronDownIcon
            className={cn(
              'h-3 w-3 transition-transform duration-200',
              isDropdownOpen ? 'rotate-180' : ''
            )}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            data-dd-action-name="instagram-creator-overview-actions-invite"
            aria-label={canBeInvited ? 'Invite creator' : getInviteDisabledReason()}
            className="cursor-pointer"
            disabled={!canBeInvited}
          >
            Invite
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <div className="p-2">
              <Input
                placeholder="Search campaigns..."
                value={campaignSearchTerm}
                onChange={(e) => setCampaignSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="mb-2"
                data-dd-action-name="instagram-creator-overview-actions-search-campaigns"
              />
              {filteredCampaigns?.map((program) => (
                <DropdownMenuItem
                  key={String(program.id)}
                  onSelect={() => {
                    onInviteToCampaign(program);
                    setCampaignSearchTerm('');
                  }}
                  className="cursor-pointer"
                  data-dd-action-name={`instagram-creator-overview-actions-select-campaign-${program.id}`}
                >
                  {program.title}
                </DropdownMenuItem>
              ))}
              {filteredCampaigns?.length === 0 && (
                <div className="p-2 text-sm text-muted-foreground">
                  No campaigns found
                </div>
              )}
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            data-dd-action-name="instagram-creator-overview-actions-add-to-group"
            aria-label="Add creator to group"
            className="cursor-pointer"
          >
            Add to Group
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <div className="p-2">
              <Input
                placeholder="Search groups..."
                value={groupSearchTerm}
                onChange={(e) => setGroupSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="mb-2"
                data-dd-action-name="instagram-creator-overview-actions-search-groups"
              />
              {filteredGroups?.map((group) => (
                <DropdownMenuItem
                  key={String(group.id)}
                  onSelect={() => {
                    onAddToGroup(group);
                    setGroupSearchTerm('');
                  }}
                  className="cursor-pointer"
                  data-dd-action-name={`instagram-creator-overview-actions-select-group-${group.id}`}
                >
                  {group.title}
                </DropdownMenuItem>
              ))}
              {filteredGroups?.length === 0 && (
                <div className="p-2 text-sm text-muted-foreground">
                  No groups found
                </div>
              )}
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSub>
          <DropdownMenuSubTrigger
            data-dd-action-name="instagram-creator-overview-actions-add-to-campaign"
            aria-label="Skip invite and add to campaign"
            className="cursor-pointer"
          >
            Skip Invite & Add to Campaign
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <div className="p-2">
              <Input
                placeholder="Search campaigns..."
                value={campaignSearchTerm}
                onChange={(e) => setCampaignSearchTerm(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                className="mb-2"
                data-dd-action-name="instagram-creator-overview-actions-search-campaigns-add"
              />
              {filteredCampaigns?.map((program) => (
                <DropdownMenuItem
                  key={`add-${String(program.id)}`}
                  onSelect={() => {
                    onAddToCampaign(program);
                    setCampaignSearchTerm('');
                  }}
                  className="cursor-pointer"
                  data-dd-action-name={`instagram-creator-overview-actions-add-to-campaign-${program.id}`}
                >
                  {program.title}
                </DropdownMenuItem>
              ))}
              {filteredCampaigns?.length === 0 && (
                <div className="p-2 text-sm text-muted-foreground">
                  No campaigns found
                </div>
              )}
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default CreatorOverviewActions;
